import { SSE_TERMINAL_EVENTS, isSseEventName, type SseEventName } from "@sagot/constants";
import { parseSSE, type ParseSseOptions, type SseMessage } from "./sse";

/** A chat stream event from the contract meta/status/token/citations/action/revise/done/error. */
export interface ChatStreamEvent {
  type: SseEventName;
  /** JSON-decoded payload; raw string when the payload is not JSON (e.g. a bare token). */
  data: unknown;
  id: string | null;
}

export function decodePayload(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return raw;
  }
}

/** Maps a raw SSE message to a contract event; unknown event names are ignored (forward compatible). */
export function toChatEvent(message: SseMessage): ChatStreamEvent | null {
  if (!isSseEventName(message.event)) return null;
  return { type: message.event, data: decodePayload(message.data), id: message.id };
}

/** Text carried by a `token` event: `{ "text": "…" }` or a bare string. */
export function tokenText(event: ChatStreamEvent): string {
  if (event.type !== "token") return "";
  if (typeof event.data === "string") return event.data;
  if (typeof event.data === "object" && event.data !== null && "text" in event.data) {
    const { text } = event.data;
    return typeof text === "string" ? text : "";
  }
  return "";
}

/** Iterates contract events and stops after the first terminal event (`done` / `error`). */
export async function* readChatEvents(
  stream: ReadableStream<Uint8Array>,
  options: ParseSseOptions = {},
): AsyncGenerator<ChatStreamEvent, void, undefined> {
  for await (const message of parseSSE(stream, options)) {
    const event = toChatEvent(message);
    if (!event) continue;
    yield event;
    if (SSE_TERMINAL_EVENTS.includes(event.type)) return;
  }
}
